import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
    Container,
    Grid,
    Box,
    Typography,
    Chip,
    Divider,
    Button,
    Paper,
    CircularProgress,
    Alert,
    Snackbar,
    Card,
    CardContent,
    Avatar,
    Link,
    IconButton,
    Menu,
    MenuItem
} from '@mui/material';
import {
    LocationOn,
    Home,
    Timer,
    Info,
    AttachMoney,
    Train,
    Square,
    Apartment,
    BathtubOutlined,
    KingBedOutlined,
    LocalLaundryServiceOutlined,
    Person,
    MoreHoriz
} from '@mui/icons-material';
import axios from 'axios';
import { createChatRoom } from '../services/firebase';
import { AMENITY_LABELS } from '../constants/amenities';
import ImageGallery from '../components/common/ImageGallery';
import ReportDialog from '../components/common/ReportDialog';
import Seo from '../components/common/Seo';
import Breadcrumbs from '../components/common/Breadcrumbs';
import { getRoomSchema, getBreadcrumbSchema } from '../utils/structuredDataHelpers';
import { getRoomDetailTitle, getRoomDetailDescription } from '../utils/seoHelpers';
import { event } from '../utils/analytics';
import { getRoom, getUser } from '../services/api';

const AVAILABILITY_LABELS = {
    flexible: '相談可能',
    short: '短期（半年以内）',
    medium: '中期（2年以内）',
    long: '長期（2年以上）'
};

const STATUS_LABELS = {
    active: '募集中',
    inactive: '募集停止',
    draft: '下書き',
    suspended: '一時停止'
};

const MOVE_IN_TIMING = {
    immediate: 'すぐに入居可',
    one_month: '1ヶ月後以降',
    three_months: '3ヶ月後以降'
};

const calculateAge = (dateOfBirth) => {
    const birthDate = new Date(dateOfBirth);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
    }

    return age;
};

function RoomDetail() {
    const { id } = useParams();
    const [room, setRoom] = useState(null);
    const [owner, setOwner] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const navigate = useNavigate();
    const { currentUser } = useAuth();
    const location = useLocation();
    const [anchorEl, setAnchorEl] = useState(null);
    const [reportDialogOpen, setReportDialogOpen] = useState(false);

    // 物件とオーナー情報を効率的に取得する統合された関数
    const fetchRoomData = useCallback(async () => {
        try {
            setLoading(true);

            // キャッシュ機能付きで物件情報を取得
            const roomData = await getRoom(id);
            setRoom(roomData);

            // オーナー情報を並行して取得（物件にオーナー情報がある場合のみ）
            if (roomData && roomData.owner) {
                const ownerId = typeof roomData.owner === 'string' ? roomData.owner : roomData.owner._id;

                try {
                    // キャッシュ機能付きでオーナー情報を取得
                    const ownerData = await getUser(ownerId);
                    setOwner(ownerData);
                } catch (ownerError) {
                    console.error('オーナー情報の取得に失敗しました:', ownerError);
                    // オーナー情報の取得に失敗しても物件情報は表示する
                }
            }

        } catch (error) {
            console.error('物件詳細の取得に失敗しました:', error);
            setError('物件の取得に失敗しました');
        } finally {
            setLoading(false);
        }
    }, [id]);

    // 物件IDが変更された時のみデータを取得
    useEffect(() => {
        fetchRoomData();
    }, [fetchRoomData]);

    const handleContactOwner = async () => {
        // Google Analyticsへイベント送信
        event({
            category: 'Contact',
            action: 'Contacted Owner',
            label: `Room: ${room.title || room._id}`,
        });

        try {
            if (!currentUser) {
                navigate('/login', {
                    state: { from: `/rooms/${id}` }  // 戻り先を保存
                });
                return;
            }
            if (!owner) {
                console.error('オーナー情報が取得できていません');
                return;
            }

            // 重複実行防止のため、ローディング状態をチェック
            if (loading) {
                return;
            }

            setLoading(true);

            const chatRoomId = await createChatRoom(
                currentUser.uid,
                owner._id
            );

            if (chatRoomId) {
                navigate(`/messages/${chatRoomId}`);
            } else {
                console.error('チャットルームの作成に失敗しました');
            }
        } catch (error) {
            console.error('チャットルーム作成エラー:', error);
            setError('チャットルームの作成に失敗しました。しばらくしてから再度お試しください。');
        } finally {
            setLoading(false);
        }
    };

    const handleCloseError = () => {
        setError('');
    };

    const handleMenuOpen = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleReportRoom = () => {
        handleMenuClose();
        setReportDialogOpen(true);
    };

    // SEO設定用の関数を更新（seoHelpersを使用）
    const getPageTitle = () => {
        if (!room) return '物件詳細';
        return getRoomDetailTitle(room);
    };

    const getPageDescription = () => {
        if (!room) return '物件詳細ページです。';
        return getRoomDetailDescription(room);
    };

    // パンくずリスト用データはそのまま
    const getBreadcrumbs = () => {
        if (!room) return null;

        return [
            { name: 'トップ', path: '/' },
            { name: 'ルームシェア物件検索', path: '/room-search' },
            { name: `${room.location.prefecture}`, path: `/room-search?location=${encodeURIComponent(room.location.prefecture)}` },
            { name: `${room.location.city}`, path: `/room-search?location=${encodeURIComponent(room.location.prefecture + ' ' + room.location.city)}` },
            { name: `${room.propertyType || '物件詳細'}`, path: `/rooms/${id}` }
        ];
    };

    // JSON-LDの配列を生成
    const getSchemaArray = () => {
        if (!room) return null;

        // 基本の物件情報とパンくずリストの構造化データを配列で結合
        const roomSchema = getRoomSchema(room);
        const breadcrumbSchema = getBreadcrumbSchema(getBreadcrumbs());

        return [roomSchema, breadcrumbSchema].filter(Boolean);
    };

    if (loading) {
        return (
            <Container maxWidth="lg">
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
                    <CircularProgress />
                </Box>
            </Container>
        );
    }

    if (!room) {
        return (
            <Container maxWidth="lg">
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
                    <Typography variant="h5" color="text.secondary">
                        物件が見つかりません
                    </Typography>
                </Box>
            </Container>
        );
    }

    return (
        <>
            {/* roomデータがある場合だけSEOコンポーネントを表示 */}
            {room && (
                <Seo
                    title={getPageTitle()}
                    description={getPageDescription()}
                    path={`/rooms/${id}`}
                    image={room.images?.length > 0 ? room.images[0] : null}
                    location={`${room.location.prefecture} ${room.location.city}`}
                    schema={getSchemaArray()}
                    isArticle={true}
                    metaRobots={room.isSample ? 'noindex, nofollow' : undefined}
                />
            )}

            <Container maxWidth="lg">
                <Snackbar
                    open={!!error}
                    autoHideDuration={6000}
                    onClose={handleCloseError}
                    anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                >
                    <Alert onClose={handleCloseError} severity="error">
                        {error}
                    </Alert>
                </Snackbar>

                {/* パンくずリストを追加 */}
                <Breadcrumbs items={getBreadcrumbs()} sx={{ mt: 3 }} />

                {/* サンプル物件の場合、通知バナーを表示 */}
                {room.isSample && (
                    <Alert
                        severity="warning"
                        sx={{
                            mt: 2,
                            mb: 2,
                            borderRadius: 2
                        }}
                    >
                        これはサンプル物件です。実際に募集している物件ではありません。
                    </Alert>
                )}

                <Box py={3}> {/* paddingを調整 */}
                    <Paper elevation={2} sx={{ mb: 4, borderRadius: 2, overflow: 'hidden' }}>
                        <ImageGallery images={room.images} height={450} />
                    </Paper>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h5" component="h1">
                            {room.location.prefecture} {room.location.city} の物件
                        </Typography>
                        <IconButton onClick={handleMenuOpen}>
                            <MoreHoriz />
                        </IconButton>
                    </Box>

                    <Grid container spacing={4}>
                        <Grid item xs={12} md={8}>
                            <Card elevation={2} sx={{ mb: 3, p: 2, borderRadius: 2 }}>
                                <CardContent>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                        <Typography variant="h4" component="div" color="primary.main" fontWeight="bold">
                                            ¥{room.rentamount.toLocaleString()}
                                        </Typography>
                                        <Typography variant="h6" color="text.secondary" sx={{ ml: 1 }}>
                                            /月
                                        </Typography>
                                    </Box>
                                </CardContent>
                            </Card>

                            <Card elevation={2} sx={{ mb: 3, borderRadius: 2 }}>
                                <CardContent>
                                    <Box sx={{ mb: 3 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                            <LocationOn sx={{ color: 'primary.main', mr: 1 }} />
                                            <Typography variant="h6">所在地</Typography>
                                        </Box>
                                        <Typography variant="body1" sx={{ mb: 1 }}>
                                            {room.location.prefecture} {room.location.city}
                                        </Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary', flexDirection: 'column', alignItems: 'flex-start' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                <Train sx={{ fontSize: 20, mr: 1 }} />
                                                <Typography>
                                                    {room.location.line}線
                                                </Typography>
                                            </Box>
                                            <Typography sx={{ pl: 3.5 }}>
                                                <Typography component="span" sx={{ fontWeight: 'medium' }}>
                                                    {room.location.station}
                                                </Typography>
                                                駅
                                                <Typography component="span" sx={{ ml: 1, color: 'primary.main', fontWeight: 'bold' }}>
                                                    徒歩{room.location.walkingMinutes}分
                                                </Typography>
                                            </Typography>
                                        </Box>
                                    </Box>
                                </CardContent>
                            </Card>

                            <Card elevation={2} sx={{ mb: 3, borderRadius: 2 }}>
                                <CardContent>
                                    <Box sx={{ mb: 3 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                            <Apartment sx={{ color: 'primary.main', mr: 1 }} />
                                            <Typography variant="h6">物件情報</Typography>
                                        </Box>
                                        <Grid container spacing={2}>
                                            <Grid item xs={6}>
                                                <Box>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                                        <Home sx={{ fontSize: 20, mr: 1, color: 'text.secondary' }} />
                                                        <Typography color="text.secondary">物件タイプ</Typography>
                                                    </Box>
                                                    <Typography sx={{ pl: 3.5, fontWeight: 'medium' }}>
                                                        {room.propertyType}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                            <Grid item xs={6}>
                                                <Box>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                                        <KingBedOutlined sx={{ fontSize: 20, mr: 1, color: 'text.secondary' }} />
                                                        <Typography color="text.secondary">部屋タイプ</Typography>
                                                    </Box>
                                                    <Typography sx={{ pl: 3.5, fontWeight: 'medium' }}>
                                                        {room.roomType}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                            <Grid item xs={6}>
                                                <Box>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                                        <Square sx={{ fontSize: 20, mr: 1, color: 'text.secondary' }} />
                                                        <Typography color="text.secondary">間取り</Typography>
                                                    </Box>
                                                    <Typography sx={{ pl: 3.5, fontWeight: 'medium' }}>
                                                        {room.roomDetails?.layout || '情報なし'}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                            <Grid item xs={6}>
                                                <Box>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                                        <Square sx={{ fontSize: 20, mr: 1, color: 'text.secondary' }} />
                                                        <Typography color="text.secondary">広さ</Typography>
                                                    </Box>
                                                    <Typography sx={{ pl: 3.5, fontWeight: 'medium' }}>
                                                        {room.roomDetails?.size ? `${room.roomDetails.size}㎡` : '情報なし'}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                        </Grid>
                                    </Box>
                                </CardContent>
                            </Card>

                            <Card elevation={2} sx={{ mb: 3, borderRadius: 2 }}>
                                <CardContent>
                                    <Box sx={{ mb: 3 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                            <BathtubOutlined sx={{ color: 'primary.main', mr: 1 }} />
                                            <Typography variant="h6">設備・アメニティ</Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                            {room.roomDetails?.amenities?.map((amenity) => (
                                                <Chip
                                                    key={amenity}
                                                    label={AMENITY_LABELS[amenity] || amenity}
                                                    variant="outlined"
                                                    sx={{
                                                        borderRadius: 2,
                                                        '&:hover': {
                                                            backgroundColor: 'primary.light',
                                                            color: 'white'
                                                        }
                                                    }}
                                                />
                                            )) || '設備情報がありません'}
                                        </Box>
                                    </Box>
                                </CardContent>
                            </Card>

                            {room.description && (
                                <Card elevation={2} sx={{ mb: 3, borderRadius: 2 }}>
                                    <CardContent>
                                        <Box sx={{ mb: 3 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                                <Info sx={{ color: 'primary.main', mr: 1 }} />
                                                <Typography variant="h6">物件の説明</Typography>
                                            </Box>
                                            <Typography sx={{ whiteSpace: 'pre-line' }}>{room.description}</Typography>
                                        </Box>
                                    </CardContent>
                                </Card>
                            )}
                        </Grid>

                        <Grid item xs={12} md={4}>
                            {owner && (
                                <Card elevation={2} sx={{ p: 3, borderRadius: 2, mb: 3 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                        <Person sx={{ color: 'primary.main', mr: 1 }} />
                                        <Typography variant="h6">募集主</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                        <Avatar
                                            src={owner.profile?.profileImage}
                                            alt={owner.profile?.nickname}
                                            sx={{ width: 64, height: 64, mr: 2 }}
                                        />
                                        <Box>
                                            <Typography variant="h6" component="div" sx={{ mb: 0.5 }}>
                                                {owner.profile?.nickname}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {owner.profile?.gender && `${owner.profile.gender === 'male' ? '男性' : '女性'}`}
                                                {owner.profile?.dateOfBirth && ` ${calculateAge(owner.profile.dateOfBirth)}歳`}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Link
                                        href={`/users/${owner._id}`}
                                        underline="none"
                                        sx={{ display: 'block', textAlign: 'right', mt: 1 }}
                                    >
                                        <Typography color="primary" variant="body2">
                                            プロフィールを見る →
                                        </Typography>
                                    </Link>
                                </Card>
                            )}

                            <Card elevation={2} sx={{ p: 3, borderRadius: 2, position: 'sticky', top: 20 }}>
                                <Box sx={{ mb: 3 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                        <Timer sx={{ color: 'primary.main', mr: 1 }} />
                                        <Typography variant="h6">契約情報</Typography>
                                    </Box>
                                    <Typography sx={{ mb: 1 }}>
                                        入居可能タイミング: {MOVE_IN_TIMING[room.moveInTiming]}
                                    </Typography>
                                    <Typography>
                                        居住可能期間: {AVAILABILITY_LABELS[room.availabilityType]}
                                    </Typography>
                                </Box>

                                {owner && (
                                    !currentUser ? (
                                        /* 未ログイン時：登録促進UI */
                                        <Box>
                                            <Alert
                                                severity="info"
                                                sx={{ mb: 2, borderRadius: 2 }}
                                            >
                                                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                                    オーナーに連絡するにはログインが必要です
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    アカウント登録は無料・3分で完了します
                                                </Typography>
                                            </Alert>
                                            <Button
                                                variant="outlined"
                                                fullWidth
                                                onClick={() => navigate('/login', { state: { from: location.pathname } })}
                                                sx={{ borderRadius: 2, mb: 1, py: 1.2, fontWeight: 'bold' }}
                                            >
                                                ログインする
                                            </Button>
                                            <Button
                                                variant="contained"
                                                fullWidth
                                                onClick={() => navigate('/register', { state: { from: location.pathname } })}
                                                sx={{ borderRadius: 2, py: 1.2, fontWeight: 'bold', boxShadow: 3 }}
                                            >
                                                無料登録してオーナーに連絡する
                                            </Button>
                                        </Box>
                                    ) : currentUser.uid === owner._id ? (
                                        /* 自分の物件の場合 */
                                        <Button
                                            variant="outlined"
                                            fullWidth
                                            disabled
                                            sx={{ borderRadius: 2, py: 1.5, fontWeight: 'bold' }}
                                        >
                                            あなたの物件です
                                        </Button>
                                    ) : (
                                        /* ログイン済み・他者の物件 */
                                        <Button
                                            variant="contained"
                                            fullWidth
                                            onClick={handleContactOwner}
                                            sx={{
                                                borderRadius: 2,
                                                py: 1.5,
                                                fontWeight: 'bold',
                                                boxShadow: 3
                                            }}
                                        >
                                            オーナーに連絡する
                                        </Button>
                                    )
                                )}
                            </Card>
                        </Grid>
                    </Grid>
                </Box>

                {/* メニュー */}
                <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleMenuClose}
                >
                    <MenuItem onClick={handleReportRoom}>
                        物件を通報
                    </MenuItem>
                </Menu>

                {/* 通報ダイアログ */}
                <ReportDialog
                    open={reportDialogOpen}
                    onClose={() => setReportDialogOpen(false)}
                    targetType="room"
                    targetId={id}
                />
            </Container>
        </>
    );
}

export default RoomDetail; 